// src/ui/arenga-card.js — card visual reusable para arengas.
//
// Misma estética en los 3 callers: muralla auto-scroll de Tribuna,
// grid 2 cols de "Mis arengas guardadas" en perfil y (a futuro) listas del
// grupo. Devuelve HTML como string para integrarse con el HTML-gen legacy
// del resto de los módulos.
//
// modes:
//   'wall'  — flex:0 0 150px height:36px (muralla auto-scroll Tribuna).
//   'grid'  — width:100% height:48px (grid 2 cols del perfil).
//
// El click NO se cablea acá — el caller pone la clase CSS y el data-id que
// quiera y wirea el handler aparte (mantiene desacoplado el routing).

import { renderPactoIcon } from './icons.js'

// Tipos canónicos de arenga (spec v1.2 §2). Idéntico al de tribuna.js y
// rituales.js. Proxy con fallback 'vamos' para keys legacy desconocidas.
const TYPES_BASE = {
  vamos:       { label: '¡Vamos todavía!',     color: '#2563eb', icon: 'M9 18V5l9-2v13M9 9l9-2M9 18a2 2 0 1 1-2-2 2 2 0 0 1 2 2ZM18 16a2 2 0 1 1-2-2 2 2 0 0 1 2 2Z' },
  opinion:     { label: 'Opinión de partido',  color: '#38bdf8', icon: 'M8 14c1.8-3.2 3.3-4.7 5.2-4.7h1.9c1.3 0 2.4 1.1 2.4 2.4v.6h-3.2M8 14l-2.2 3.2M8 14h6.7c1.5 0 2.8 1.2 2.8 2.8V18H9.6M12.5 9.4V6.8c0-1.3 1-2.3 2.3-2.3h.8v4.8' },
  sufrimiento: { label: 'Sufrimiento',         color: '#8b5cf6', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.1l3.1 2.25-1.2 3.65h-3.8L8.9 9.35 12 7.1Z' },
  humor:       { label: 'Humor',               color: '#f59e0b', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z' },
  festejo:     { label: 'Festejo/Fiesta',      color: '#22c55e', icon: 'M6 20l3-10 5 5-8 5ZM14 4l.4 2.4L17 7l-2.6.6L14 10l-.4-2.4L11 7l2.6-.6L14 4Z' },
  canticos:    { label: 'Cánticos',            color: '#4f46e5', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z' },
  bronca:      { label: 'Bronca/Desilusión',   color: '#991b1b', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9l2 1M16 9l-2 1' },
  declaracion: { label: 'Declaración',         color: '#fbbf24', icon: 'M5 6h14v9H9l-4 4V6ZM8 9h8M8 12h6' },
}
const ARENGA_TYPES = new Proxy({ ...TYPES_BASE }, { get: (t, k) => t[k] || t.vamos })

const escapeHtmlAttr = (s) => String(s || '').replace(/"/g, '&quot;')

// renderArengaCard({ arenga, mode, className, clickHandler, secondaryLine })
//
// arenga es el shape extendido del feed: { id, tipo, titulo, duracion, grupo, reacCount, ago }
// Si no se le pasa un campo, se rellena con defaults razonables.
//
// className: clase CSS extra (ej '.tribuna-audio-open', '.perfil-arenga-guardada-card').
// clickHandler: inline string (typically `onclick="window.location.hash=..."`) o vacío.
// secondaryLine: opcional, lo que va en la segunda línea de la card (override del default).
export const renderArengaCard = ({ arenga, mode = 'wall', className = '', clickHandler = '', secondaryLine = null }) => {
  const meta = ARENGA_TYPES[arenga.tipo]
  const duracion = arenga.duracion || `0:${String(arenga.segundos || 0).padStart(2, '0')}`
  const grupo = (arenga.grupoOrigen || arenga.provincia || 'Tribuna').toUpperCase()
  const isWall = mode === 'wall'
  const widthStyle = isWall ? 'flex:0 0 150px' : 'width:100%'
  const heightStyle = isWall ? 'height:36px' : 'min-height:48px;height:48px'
  const iconSize = isWall ? '18px' : '24px'
  const iconRadius = isWall ? '7px' : '8px'
  const iconPad = isWall ? 11 : 14
  const padding = isWall ? '0.28rem 0.38rem' : '0.4rem 0.5rem'
  const fontMain = isWall ? '0.58rem' : '0.7rem'
  const fontSecondary = isWall ? '0.54rem' : '0.66rem'
  const fontMeta = isWall ? '0.52rem' : '0.62rem'

  // pts: número opcional con los puntos acumulados por la arenga (creación +
  // curva §5.1). Si está presente, se renderiza al lado de reacciones.
  const ptsBadge = (arenga.pts === undefined || arenga.pts === null)
    ? ''
    : `<span data-rit-pts="arenga:${arenga.id}" data-rit-reac="${arenga.reacCount ?? 0}" style="color:#7ab488;font-size:${fontMeta};line-height:1;white-space:nowrap;">Puntos: ${arenga.pts > 0 ? '+' : ''}${arenga.pts}</span>`
  const secondaryDefault = `
    <strong data-arenga-reac="${escapeHtmlAttr(arenga.id)}" data-arenga-reac-count="${arenga.reacCount ?? 0}" style="color:${meta.color};font-size:${fontMeta};line-height:1;white-space:nowrap;">${arenga.reacCount ?? 0} reac.</strong>
    ${ptsBadge}
    <em style="color:var(--text-muted);font-style:normal;font-size:${fontMeta};line-height:1;white-space:nowrap;">${escapeHtmlAttr(arenga.ago || '')}</em>
  `
  const secondaryHTML = secondaryLine !== null ? secondaryLine : secondaryDefault

  return `
    <button type="button" class="${className}" data-id="${escapeHtmlAttr(arenga.id)}" ${clickHandler} style="${widthStyle};${heightStyle};border:1px solid rgba(255,255,255,0.11);border-radius:9px;background:radial-gradient(circle at 14% 0%, ${meta.color}34, transparent 48%), rgba(9,14,24,0.84);color:var(--white);padding:${padding};display:flex;align-items:center;gap:0.34rem;text-align:left;cursor:pointer;box-shadow:0 0 7px ${meta.color}14,inset 0 1px 0 rgba(255,255,255,0.08);position:relative;z-index:2;pointer-events:auto;">
      <span style="width:${iconSize};height:${iconSize};border-radius:${iconRadius};display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(135deg, ${meta.color}, rgba(255,255,255,0.16));flex:0 0 auto;">${renderPactoIcon(meta.icon, '#ffffff', iconPad)}</span>
      <span style="min-width:0;flex:1;display:flex;flex-direction:column;gap:0.06rem;">
        <span style="display:flex;align-items:center;gap:0.22rem;min-width:0;">
          <strong style="color:var(--white);font-size:${fontMain};line-height:1;white-space:nowrap;">&#127897; ${duracion}</strong>
          <em style="color:var(--text-muted);font-style:normal;font-size:${fontSecondary};line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;">${escapeHtmlAttr(grupo)}</em>
        </span>
        <span style="display:flex;align-items:center;gap:0.22rem;min-width:0;">
          ${secondaryHTML}
        </span>
      </span>
    </button>
  `
}

// Re-export del meta lookup para casos donde el caller necesita el color/label
// del tipo (ej. el modal de despliegue que pinta el badge grande).
export { ARENGA_TYPES }
